/**
 * Czech labels and formatters for the rule-set module (Modul 3).
 *
 * The label maps are the single source of truth for the enum wording —
 * see the terminology table in AGENTS.md — and the formatters own the two
 * conversions the API deliberately leaves to the client: integer
 * centipoints → Czech decimals, and seconds → m:ss.
 */
import { formatDuration } from "@marekl/adminer/format";
import type {
  AgeAggregation,
  AgeMode,
  DeductionUnit,
  MusicSource,
  ResultFormula,
  RuleSetStatus,
} from "./queries/rules";

type BadgeVariant = "ok" | "warn" | "err" | "info" | "muted";

export const RULESET_STATUS_LABELS: Record<RuleSetStatus, string> = {
  draft: "Rozpracovaná",
  active: "Aktivní",
  archived: "Archivovaná",
};

export const RULESET_STATUS_VARIANT: Record<RuleSetStatus, BadgeVariant> = {
  draft: "muted",
  active: "ok",
  archived: "warn",
};

export const AGE_MODE_LABELS: Record<AgeMode, string> = {
  fixed_date: "K pevnému datu v roce soutěže",
  at_competition: "Skutečný věk v den soutěže",
};

export const AGE_AGGREGATION_LABELS: Record<AgeAggregation, string> = {
  own: "Vlastní věk",
  oldest: "Věk nejstaršího",
  average: "Průměrný věk",
};

export const MUSIC_SOURCE_LABELS: Record<MusicSource, string> = {
  own: "Vlastní hudba",
  federation: "Hudba ČNSMS",
  none: "Hudbu zajišťuje zvukař",
};

export const RESULT_FORMULA_LABELS: Record<ResultFormula, string> = {
  average: "Průměr známek",
  sum: "Součet známek",
};

export const DEDUCTION_UNIT_LABELS: Record<DeductionUnit, string> = {
  occurrence: "za výskyt",
  second: "za započatou sekundu",
};

/**
 * Formats integer centipoints as Czech decimal points (1000 → "10,00").
 * Values are stored as integers so the backend can add and subtract
 * scores exactly; the decimal point exists only for display.
 */
export function formatPoints(cp: number): string {
  const sign = cp < 0 ? "−" : "";
  const abs = Math.abs(cp);
  return `${sign}${Math.floor(abs / 100)},${String(abs % 100).padStart(2, "0")}`;
}

/**
 * Formats a deduction. Deductions are stored as positive magnitudes, so
 * the minus sign belongs here rather than in the data.
 */
export function formatDeduction(cp: number, unit?: DeductionUnit): string {
  const value = `−${formatPoints(Math.abs(cp))} b`;
  return unit === "second" ? `${value}/s` : value;
}

/**
 * Formats a duration in seconds as m:ss (90 → "1:30").
 *
 * A wrapper rather than the bare import for one reason: the sign. The lib
 * emits an ASCII hyphen, and every other signed value in this file carries
 * U+2212 (`formatPoints`, `formatDeduction`), so a negative would be the one
 * place the rule set writes its minus differently. `format: "ms"` keeps a
 * time limit from rolling into hours the way the default would.
 */
export function formatSeconds(seconds: number): string {
  const sign = seconds < 0 ? "−" : "";
  return `${sign}${formatDuration(Math.abs(seconds), { format: "ms" })}`;
}

/**
 * Renders a resolved time limit. The three shapes carry real, different
 * rules: a zero minimum means there is no lower limit, and equal bounds
 * mean a fixed duration.
 */
export function formatTimeRange(
  minSeconds: number | null | undefined,
  maxSeconds: number | null | undefined,
): string {
  const hasMin = minSeconds !== null && minSeconds !== undefined;
  const hasMax = maxSeconds !== null && maxSeconds !== undefined;
  if (!hasMin && !hasMax) return "—";
  if (!hasMax) return `od ${formatSeconds(minSeconds as number)}`;
  if (!hasMin || minSeconds === 0) return `do ${formatSeconds(maxSeconds as number)}`;
  if (minSeconds === maxSeconds) return `${formatSeconds(maxSeconds as number)} (pevný)`;
  return `${formatSeconds(minSeconds as number)}–${formatSeconds(maxSeconds as number)}`;
}

/**
 * Parses an m:ss or plain-seconds time input. Returns null for anything
 * unparseable so a caller can keep the field in an error state rather
 * than silently storing a wrong limit.
 */
export function parseTimeInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const match = /^(\d+):([0-5]\d)$/.exec(trimmed);
  if (match) return Number(match[1]) * 60 + Number(match[2]);
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  return null;
}

/**
 * Parses a points input into centipoints, accepting both the Czech
 * comma and a dot ("0,10" → 10). Returns null for anything with more
 * than two decimals or any other unparseable value, so a typo cannot
 * silently become a different penalty.
 */
export function parsePointsInput(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

/** Renders a headcount band ("8 a více", "2–3", "1"). */
export function formatCountRange(minCount: number, maxCount: number | null): string {
  if (maxCount === null) return `${minCount} a více`;
  if (maxCount === minCount) return String(minCount);
  return `${minCount}–${maxCount}`;
}

/**
 * Renders an age band the way the rulebook writes it: "do 7 let",
 * "12–14 let", "18+ let". Bounds are inclusive, which the wording
 * already conveys — nothing here spells it out.
 */
export function formatAgeRange(minAge: number | null, maxAge: number | null): string {
  if (minAge === null && maxAge === null) return "bez omezení";
  if (minAge === null) return `do ${maxAge} let`;
  if (maxAge === null) return `${minAge}+ let`;
  if (minAge === maxAge) return `${minAge} let`;
  return `${minAge}–${maxAge} let`;
}
